import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

export default function FormateursPage() {
  // SEO Configuration
  useSEO({
    title: 'Formateurs et Centres de Formation - Supports PowerPoint Pédagogiques | Izislide',
    description: 'Imagez vos idées : Designer PowerPoint expert pour formateurs et centres de formation. Création de supports de formation PowerPoint clairs, engageants et pédagogiques. Templates PPT modulables.',
    keywords: 'support formation powerpoint, designer powerpoint formation, présentation pédagogique, création ppt formation, supports de cours powerpoint',
    canonical: '/formateurs',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Supports PowerPoint pour Formateurs et Centres de Formation',
      description: 'Création de supports de formation PowerPoint professionnels et pédagogiques',
      provider: {
        '@type': 'Organization',
        name: 'Izislide',
        slogan: 'Imagez vos idées'
      },
      areaServed: 'FR',
      serviceType: 'Création de supports de formation PowerPoint',
      offers: [
        {
          '@type': 'Offer',
          name: 'Pack Essentiel',
          price: '390',
          priceCurrency: 'EUR'
        },
        {
          '@type': 'Offer',
          name: 'Pack Pro',
          price: '490',
          priceCurrency: 'EUR'
        },
        {
          '@type': 'Offer',
          name: 'Pack Premium',
          price: '1200',
          priceCurrency: 'EUR'
        }
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-24 overflow-hidden bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0,168,107,0.85) 0%, rgba(0,143,92,0.85) 100%), url('https://public.readdy.ai/ai/img_res/be469fb95a4207b7bb6574f136b7004f.jpg')`
        }}
      >
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Formateurs et Centres de Formation : Des supports PowerPoint pédagogiques clairs et engageants, à grande échelle
            </h1>
            <p className="text-xl md:text-2xl mb-10 opacity-90">
              <strong>Imagez vos idées</strong> : Notre designer PowerPoint crée vos supports de formation professionnels
            </p>
            <a 
              href="#solution" 
              className="inline-block bg-white text-[#00A86B] px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Je veux professionnaliser mes supports PowerPoint
            </a>
          </div>
        </div>
      </section>

      {/* Pain Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              Les défis des formateurs et centres de formation
            </h2>
            <div className="bg-white p-10 rounded-xl shadow-lg border-l-4 border-red-400">
              <div className="space-y-6 text-lg text-gray-700">
                <p className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-4 mt-1"></i>
                  <span>Vos <strong>supports PowerPoint de formation</strong> sont trop denses et peu engageants</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-4 mt-1"></i>
                  <span>Vous passez plus de temps sur vos <strong>présentations PPT</strong> que sur la pédagogie</span>
                </p>
                <p className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-4 mt-1"></i>
                  <span>Difficile de maintenir une cohérence visuelle entre tous vos <strong>supports PowerPoint</strong></span>
                </p>
                <p className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-4 mt-1"></i>
                  <span>Vos stagiaires décrochent car vos <strong>présentations PowerPoint</strong> manquent de clarté visuelle</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="solution" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12 text-center">
              Des supports PowerPoint pédagogiques qui engagent vos apprenants
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="bg-gray-50 p-8 rounded-xl">
                <div className="w-16 h-16 bg-[#00A86B]/10 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-book-open-line text-3xl text-[#00A86B]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Supports PowerPoint pédagogiques</h3>
                <p className="text-gray-700">
                  <strong>Présentations PowerPoint</strong> claires, visuelles, adaptées à l'apprentissage. Notre designer PowerPoint maîtrise la pédagogie visuelle
                </p>
              </div>

              <div className="bg-gray-50 p-8 rounded-xl">
                <div className="w-16 h-16 bg-[#F7A348]/10 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-layout-grid-line text-3xl text-[#F7A348]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Templates PowerPoint modulables</h3>
                <p className="text-gray-700">
                  Créez vos propres <strong>présentations PPT</strong> facilement avec nos templates personnalisés pour votre centre de formation
                </p>
              </div>

              <div className="bg-gray-50 p-8 rounded-xl">
                <div className="w-16 h-16 bg-[#00A86B]/10 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-slideshow-line text-3xl text-[#00A86B]"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Supports PowerPoint interactifs</h3>
                <p className="text-gray-700">
                  Animations, quiz intégrés, exercices visuels dans vos <strong>présentations PowerPoint</strong> de formation
                </p>
              </div>
            </div>

            {/* Highlight Reel */}
            <div className="bg-[#C9D86A]/10 p-10 rounded-xl mb-16">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                L'impact sur vos formations
              </h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-[#00A86B] mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Formations plus engageantes</h4>
                    <p className="text-gray-700">Vos <strong>supports PowerPoint</strong> captent l'attention et maintiennent l'engagement</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-[#00A86B] mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Meilleure mémorisation</h4>
                    <p className="text-gray-700">Visuels clairs = concepts mieux compris et retenus grâce au design PowerPoint pédagogique</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-[#00A86B] mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Image professionnelle renforcée</h4>
                    <p className="text-gray-700">Vos <strong>présentations PowerPoint</strong> reflètent la qualité de votre centre de formation</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-2xl text-[#00A86B] mr-4 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Gain de temps massif</h4>
                    <p className="text-gray-700">Concentrez-vous sur la pédagogie, notre designer PowerPoint s'occupe du design</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Process */}
            <div className="bg-white p-10 rounded-xl shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Comment ça marche pour votre centre de formation ?
              </h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#F7A348] rounded-full flex items-center justify-center text-white font-bold text-xl mr-6 flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Vous nous partagez votre contenu pédagogique</h4>
                    <p className="text-gray-700">Objectifs d'apprentissage, contenus, exercices - nous adaptons tout en PowerPoint</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#00A86B] rounded-full flex items-center justify-center text-white font-bold text-xl mr-6 flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Notre designer PowerPoint crée vos supports de formation</h4>
                    <p className="text-gray-700">Design pédagogique, visuels clairs, structure optimale. <strong>Imagez vos idées</strong> pédagogiques</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#C9D86A] rounded-full flex items-center justify-center text-gray-900 font-bold text-xl mr-6 flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Livraison de vos présentations PowerPoint</h4>
                    <p className="text-gray-700">Supports PowerPoint finalisés + templates modulables pour vos futures formations</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Nos offres pour formateurs et centres de formation
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-gray-200 hover:border-[#00A86B] transition-all">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Pack Essentiel</h3>
              <div className="text-4xl font-bold text-[#00A86B] mb-6">390€</div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-[#00A86B] mr-3 mt-1"></i>
                  <span>1 <strong>présentation PowerPoint</strong> de formation (jusqu'à 20 slides)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-[#00A86B] mr-3 mt-1"></i>
                  <span>Design PowerPoint pédagogique</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-[#00A86B] mr-3 mt-1"></i>
                  <span>2 allers-retours</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-[#00A86B] mr-3 mt-1"></i>
                  <span>Livraison 72h</span>
                </li>
              </ul>
              <a 
                href="/contact" 
                className="block w-full text-center bg-[#00A86B] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#008f5c] transition-all whitespace-nowrap cursor-pointer"
              >
                Commander
              </a>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-xl border-2 border-[#F7A348] relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#F7A348] text-white px-4 py-1 rounded-full text-sm font-bold whitespace-nowrap">
                Le plus populaire
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Pack Pro</h3>
              <div className="text-4xl font-bold text-[#00A86B] mb-6">490€</div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-[#00A86B] mr-3 mt-1"></i>
                  <span>1 <strong>présentation PowerPoint</strong> de formation (jusqu'à 40 slides)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-[#00A86B] mr-3 mt-1"></i>
                  <span>Design PowerPoint pédagogique avancé</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-[#00A86B] mr-3 mt-1"></i>
                  <span>Animations et interactions</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-[#00A86B] mr-3 mt-1"></i>
                  <span>3 allers-retours</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-[#00A86B] mr-3 mt-1"></i>
                  <span>Livraison 48h</span>
                </li>
              </ul>
              <a 
                href="/contact" 
                className="block w-full text-center bg-[#F7A348] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#e89338] transition-all whitespace-nowrap cursor-pointer"
              >
                Commander
              </a>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-gray-200 hover:border-[#00A86B] transition-all">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Pack Premium</h3>
              <div className="text-4xl font-bold text-[#00A86B] mb-6">1200€</div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-[#00A86B] mr-3 mt-1"></i>
                  <span>Module complet de formation PowerPoint (jusqu'à 100 slides)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-[#00A86B] mr-3 mt-1"></i>
                  <span>Template PowerPoint personnalisé pour votre centre</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-[#00A86B] mr-3 mt-1"></i>
                  <span>Designer PowerPoint dédié</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-[#00A86B] mr-3 mt-1"></i>
                  <span>Allers-retours illimités</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-[#00A86B] mr-3 mt-1"></i>
                  <span>Support prioritaire</span>
                </li>
              </ul>
              <a 
                href="/contact" 
                className="block w-full text-center bg-[#00A86B] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#008f5c] transition-all whitespace-nowrap cursor-pointer"
              >
                Commander
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Des formateurs et centres de formation qui nous font confiance
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#00A86B] rounded-full flex items-center justify-center text-white font-bold text-xl">
                  AD
                </div>
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Anne Dubois</div>
                  <div className="text-sm text-gray-600">Formatrice Indépendante</div>
                </div>
              </div>
              <p className="text-gray-700 italic mb-4">
                "Mes <strong>supports PowerPoint de formation</strong> sont maintenant professionnels et mes stagiaires sont beaucoup plus engagés. <strong>Imagez vos idées</strong>, c'est exactement ça !"
              </p>
              <div className="flex text-[#F7A348]">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#F7A348] rounded-full flex items-center justify-center text-white font-bold text-xl">
                  JM
                </div>
                <div className="ml-4">
                  <div className="font-bold text-gray-900">Jean Martin</div>
                  <div className="text-sm text-gray-600">Directeur Centre de Formation</div>
                </div>
              </div>
              <p className="text-gray-700 italic mb-4">
                "Le template PowerPoint créé par Izislide nous fait gagner un temps fou. Tous nos formateurs peuvent maintenant créer des <strong>présentations PowerPoint</strong> cohérentes et professionnelles."
              </p>
              <div className="flex text-[#F7A348]">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 bg-gradient-to-r from-[#00A86B] to-[#008f5c]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Prêt à professionnaliser vos supports PowerPoint de formation ?
            </h2>
            <p className="text-xl mb-10 opacity-90">
              Discutons de vos besoins pédagogiques et créons ensemble des <strong>présentations PowerPoint</strong> qui engagent
            </p>
            <a 
              href="/contact" 
              className="inline-block bg-white text-[#00A86B] px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Je veux professionnaliser mes supports PowerPoint
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}